import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Col, Form, message, Row, Space } from 'antd';
import { api, endpoints } from '../../../../api';
import {
  AppearBlock,
  CloseButton,
  DownloadCol,
  FormInput,
  FormItem,
  FormNumberInput,
  ValidatorFormItem,
} from '../../../../styles/styled';
import { diplomRegExp, educationLevelRegExp, specializationRegExp } from '../../../../utils/patterns';
import { UploadComponent } from '../../../NewApplicationPage/components/BookRegistrationComponent/components/BookForm/UploadComponent';
import { FileEditorData } from '../../../NewApplicationPage/components/BookRegistrationComponent/components/BookForm/UploadComponent/UploadComponent';
import { FileDataType } from '../../../NewApplicationPage/types';
import { EducationInfoType } from '../../NewExpertPage';
import { ExpertEducationalInstitution, ExpertInstitutionsValidatorType } from '../../types';

type EducationInfoProps = {
  updateEducationalInfo: (educationForm: EducationInfoType) => void;
  id: number;
  readyEducationalInfo?: ExpertEducationalInstitution;
  validator?: ExpertInstitutionsValidatorType;
};

export const EducationInfoComponent: FC<EducationInfoProps> = ({
  validator,
  readyEducationalInfo,
  updateEducationalInfo,
  id,
}) => {
  const [expertInstitutionState, setExpertInstitution] = useState<ExpertEducationalInstitution>(
    readyEducationalInfo || {},
  );

  const [fileLoadState, setFileLoad] = useState<FileDataType | null>(null);
  const firstUpdate = useRef(false);

  const uploadFiles = useCallback(async (fileData: FileDataType) => {
    try {
      const response = await api
        .post<{ success: boolean; file_id: number }>(endpoints.addFile(), {
          ...fileData,
          ext: fileData.title.split('.').pop(),
        })
        .then((el) => {
          return el;
        });

      if (response) {
        setExpertInstitution((prevState) => {
          return { ...prevState, file: { id: response.data.file_id } };
        });

        setFileLoad({ title: fileData.title, base64: fileData.base64, ext: fileData.ext });
      }
    } catch (err) {
      message.error('Произошла ошибка загрузки файла');
    }
  }, []);

  const beforeUploadFile = async (data: FileEditorData) => {
    await uploadFiles({
      base64: data.data.split('base64,')[1],
      title: data.name as string,
      ext: data.type as string,
    });
  };

  useEffect(() => {
    if (firstUpdate.current) {
      updateEducationalInfo({
        type: 'update',
        id: id,
        expertEducational: expertInstitutionState,
      });
    }

    firstUpdate.current = true;

    // eslint-disable-next-line
  }, [expertInstitutionState]);

  return (
    <AppearBlock>
      <CloseButton
        positioned
        onClick={() =>
          updateEducationalInfo({
            type: 'remove',
            id: id,
            expertEducational: {},
          })
        }
      >
        +
      </CloseButton>
      <Row gutter={[20, 20]}>
        <Col span={5}>
          <ValidatorFormItem
            validator={!educationLevelRegExp.test(expertInstitutionState?.level_of_education || '')}
            redstar
          >
            <p>Уровень образования</p>
            <FormInput
              value={expertInstitutionState.level_of_education}
              onChange={(e) =>
                setExpertInstitution({
                  ...expertInstitutionState,
                  level_of_education: e.target.value,
                })
              }
            />
          </ValidatorFormItem>
        </Col>
        <Col span={20}>
          <FormItem redstar>
            <p>Наименование учебного заведения</p>

            <Form.Item rules={[{ required: true }]}>
              <FormInput
                error={validator && !validator?.name}
                value={expertInstitutionState.name}
                onChange={(e) =>
                  setExpertInstitution({
                    ...expertInstitutionState,
                    name: e.target.value,
                  })
                }
              />
            </Form.Item>
          </FormItem>
        </Col>
        <Col span={4}>
          <FormItem>
            <p>Год окончания</p>
            <FormNumberInput
              value={expertInstitutionState.year}
              type="number"
              controls={false}
              min={1970}
              max={2026}
              onChange={(e) =>
                setExpertInstitution({
                  ...expertInstitutionState,
                  year: e as number,
                })
              }
            />
          </FormItem>
        </Col>
        <Col span={24}>
          <ValidatorFormItem redstar validator={!specializationRegExp.test(expertInstitutionState?.specialty || '')}>
            <p>Специальность</p>
            <FormInput
              error={validator && !validator?.specialty}
              value={expertInstitutionState.specialty}
              onChange={(e) =>
                setExpertInstitution({
                  ...expertInstitutionState,
                  specialty: e.target.value,
                })
              }
            />
          </ValidatorFormItem>
        </Col>
        <Col span={12}>
          <FormItem>
            <p>Специализация</p>
            <FormInput
              value={expertInstitutionState.specialization}
              onChange={(e) =>
                setExpertInstitution({
                  ...expertInstitutionState,
                  specialization: e.target.value,
                })
              }
            />
          </FormItem>
        </Col>
        <Col span={12}>
          <FormItem redstar>
            <p>Квалификация</p>

            <Form.Item rules={[{ required: true }]}>
              <FormInput
                value={expertInstitutionState.qualification}
                error={validator && !validator?.qualification}
                onChange={(e) =>
                  setExpertInstitution({
                    ...expertInstitutionState,
                    qualification: e.target.value,
                  })
                }
              />
            </Form.Item>
          </FormItem>
        </Col>
        <Col span={12}>
          <FormItem>
            <p>Направление подготовки</p>
            <FormInput
              value={expertInstitutionState.direction_of_preparation}
              onChange={(e) =>
                setExpertInstitution({
                  ...expertInstitutionState,
                  direction_of_preparation: e.target.value,
                })
              }
            />
          </FormItem>
        </Col>
        <Col span={12}>
          <FormItem>
            <p>Профиль</p>
            <FormInput
              value={expertInstitutionState.profile}
              onChange={(e) =>
                setExpertInstitution({
                  ...expertInstitutionState,
                  profile: e.target.value,
                })
              }
            />
          </FormItem>
        </Col>
        <Col span={12}>
          <FormItem redstar>
            <p>№ диплома</p>
            <ValidatorFormItem validator={!diplomRegExp.test(expertInstitutionState?.diplom_number || '')}>
              <FormInput
                value={expertInstitutionState.diplom_number}
                error={validator && !validator?.diplom_number}
                onChange={(e) =>
                  setExpertInstitution({
                    ...expertInstitutionState,
                    diplom_number: e.target.value,
                  })
                }
              />
            </ValidatorFormItem>
          </FormItem>
        </Col>

        <DownloadCol span={24} style={{ paddingLeft: '35px' }}>
          <Space align="center">
            <UploadComponent
              onClear={() => setFileLoad(null)}
              value={
                fileLoadState && readyEducationalInfo?.file
                  ? { title: fileLoadState.title, id: readyEducationalInfo?.file.id }
                  : undefined
              }
              formats={['.pdf']}
              onChange={beforeUploadFile}
            />
          </Space>
          <p>рекомендуется прикреплять файл в формате .pdf</p>
        </DownloadCol>
      </Row>
    </AppearBlock>
  );
};
